import type {
  ChatMessage,
  ControlStreamEvent,
  FailedMessage,
  FailedMessageStreamEvent,
  ImageStreamEvent,
  ModelMessage,
  ProgressStreamEvent,
  StreamDataChunk,
  StreamEvent,
  TextStreamEvent,
} from '@Components/ai-chat-window/ai-chat-window.types';
import {FailedMessageType, MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';

export interface StreamHandlers {
  onControlEvent: (event: ControlStreamEvent) => void;
  onProgressEvent: (event: ProgressStreamEvent) => void;
  onTextStreamEvent: (event: TextStreamEvent) => void;
  onImageStreamEvent: (event: ImageStreamEvent) => void;
  onFailedMessageEvent: (event: FailedMessageStreamEvent) => void;
  onCloseEvent: () => void;
}

export const handleStreamEvent = (streamEvent: StreamDataChunk, handlers: StreamHandlers): void => {
  switch (streamEvent.event) {
    case 'control':
      handlers.onControlEvent(streamEvent);
      break;
    case 'thinking':
      handlers.onProgressEvent(streamEvent);
      break;
    case 'chat':
    case 'response':
      handlers.onTextStreamEvent(streamEvent);
      break;
    case 'image':
      handlers.onImageStreamEvent(streamEvent);
      break;
    case 'close':
      handlers.onCloseEvent();
      break;
    case 'failed_message':
      handlers.onFailedMessageEvent(streamEvent);
      break;
    default:
      console.warn('Unknown stream event type:', streamEvent);
  }
};

export const deepCopyMessage = (message: ChatMessage): ChatMessage => {
  return {
    ...message,
    parts: message.parts?.map((part) => ({...part})),
  };
};

export const createThinkingMessage = (text: string): ModelMessage => {
  return {
    role: MessageRole.ASSISTANT,
    isThinking: true,
    parts: [{type: 'text', text}],
  } as ModelMessage;
};

export const createStoppedErrorMessage = (): FailedMessage => {
  return {
    failureType: FailedMessageType.STOPPED,
    role: MessageRole.DEVELOPER,
    parts: [],
  };
};

export const updateMessageWithTextStream = (existingMessage: ChatMessage, incomingMessage: StreamEvent): ChatMessage => {
  const updatedMessage = deepCopyMessage(existingMessage);
  const incomingParts = incomingMessage.message?.parts ?? [];
  const incomingTextPart = incomingParts.find((part) => part.type === 'text');

  if (incomingTextPart) {
    const existingParts = updatedMessage.parts ?? [];
    let lastTextPartIndex = -1;

    for (let i = existingParts.length - 1; i >= 0; i--) {
      if (existingParts[i].type === 'text') {
        lastTextPartIndex = i;
        break;
      }
    }

    if (lastTextPartIndex >= 0) {
      const textPart = existingParts[lastTextPartIndex] as {type: 'text'; text: string};
      textPart.text += incomingTextPart.text;
    } else {
      updatedMessage.parts = [...existingParts, {type: 'text', text: incomingTextPart.text}];
    }
  }

  const nonTextParts = incomingParts.filter((part) => part.type !== 'text');
  if (nonTextParts.length > 0) {
    updatedMessage.parts = [...(updatedMessage.parts ?? []), ...nonTextParts];
  }

  return updatedMessage;
};

export const updateMessageWithImageStream = (existingMessage: ChatMessage, incomingMessage: StreamEvent): ChatMessage => {
  const updatedMessage = deepCopyMessage(existingMessage);
  const incomingParts = incomingMessage.message?.parts ?? [];
  const incomingImagePart = incomingParts.find((part) => part.type === 'image');

  if (incomingImagePart) {
    updatedMessage.parts = [...(updatedMessage.parts ?? []), incomingImagePart];
  }

  return updatedMessage;
};
